import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

import APIKit from "../../config/APIKit";

export const fetchProducts = createAsyncThunk(
  "product/fetchProducts",
  async ({ categoryID, page = 1 }, { rejectWithValue }) => {
    try {
      const response = await APIKit.get(`product/list/${categoryID}`, {
        params: {
          page: page,
        },
      });
      return response.data;
    } catch (error) {
      if (error && typeof error === "object" && error.response) {
        return rejectWithValue(error.response?.data || "Products get failed");
      }
      return rejectWithValue("Products get failed");
    }
  }
);

export const searchProducts = createAsyncThunk(
  "product/searchProducts",
  async ({ text }, { rejectWithValue }) => {
    try {
      const response = await APIKit.get(`product/search`, {
        params: {
          q: text,
        },
      });
      return response.data;
    } catch (error) {
      if (error && typeof error === "object" && error.response) {
        return rejectWithValue(error.response?.data || "Search failed");
      }
      return rejectWithValue("Search failed");
    }
  }
);

export const fetchOfferProducts = createAsyncThunk(
  "product/fetchOfferProducts",
  async ({ text }, { rejectWithValue }) => {
    try {
      const response = await APIKit.get(`offer/products`);
      return response.data;
    } catch (error) {
      if (error && typeof error === "object" && error.response) {
        return rejectWithValue(error.response?.data || "Fetch offer failed");
      }
      return rejectWithValue("Fetch offer failed");
    }
  }
);

const productSlice = createSlice({
  name: "product",
  initialState: {
    products: [],
    status: "idle",
    error: null,
    isSearchMode: false,
    isOffersMode: false,
    searchQuery: "",
    selectedCategoryId: null,
    pagination: {
      currentPage: 1,
      totalPages: 1,
      totalItems: 0,
      itemsPerPage: 10,
    },
  },
  reducers: {
    clearProducts: (state) => {
      state.products = [];
      state.status = "idle";
      state.error = null;
      state.isSearchMode = false;
      state.isOffersMode = false;
      state.searchQuery = "";
      state.pagination = {
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        itemsPerPage: 10,
      };
    },
    setSearchMode: (state, action) => {
      state.isSearchMode = action.payload;
    },
    clearSearchMode: (state) => {
      state.isSearchMode = false;
      state.isOffersMode = false;
      state.searchQuery = "";
    },
    setOffersMode: (state, action) => {
      state.isOffersMode = action.payload;
      state.isSearchMode = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchProducts.pending, (state, action) => {
        state.status = "loading";
        state.error = null;
        state.isSearchMode = false;
        state.isOffersMode = false;
        state.selectedCategoryId = action.meta.arg.categoryID;
      })
      .addCase(fetchProducts.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.isSearchMode = false;
        state.isOffersMode = false;
        // Store the selected category ID from the request
        state.selectedCategoryId = action.meta.arg.categoryID;
        // Handle both array and paginated response
        if (Array.isArray(action.payload)) {
          state.products = action.payload;
        } else if (action.payload.data && Array.isArray(action.payload.data)) {
          state.products = action.payload.data;
          // Update pagination metadata if available
          if (action.payload.pagination) {
            state.pagination = {
              currentPage: action.payload.pagination.currentPage || 1,
              totalPages: action.payload.pagination.totalPages || 1,
              totalItems: action.payload.pagination.totalItems || 0,
              itemsPerPage: action.payload.pagination.itemsPerPage || 10,
            };
          }
        } else {
          state.products = action.payload;
        }
        state.error = null;
      })
      .addCase(fetchProducts.rejected, (state, action) => {
        state.status = "failed";
        state.isSearchMode = false;
        state.isOffersMode = false;
        state.error =
          typeof action.payload === "string"
            ? action.payload
            : "Products get failed";
      })
      .addCase(searchProducts.pending, (state, action) => {
        state.status = "loading";
        state.error = null;
        state.isSearchMode = true;
        state.isOffersMode = false;
        state.selectedCategoryId = null; // Clear category selection in search mode
        state.searchQuery = action.meta.arg.text; // Store the search query
      })
      .addCase(searchProducts.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.isSearchMode = true;
        state.isOffersMode = false;
        // Handle both array and paginated response for search
        if (Array.isArray(action.payload)) {
          state.products = action.payload;
        } else if (action.payload.data && Array.isArray(action.payload.data)) {
          state.products = action.payload.data;
          // Update pagination metadata if available
          if (action.payload.pagination) {
            state.pagination = {
              currentPage: action.payload.pagination.currentPage || 1,
              totalPages: action.payload.pagination.totalPages || 1,
              totalItems: action.payload.pagination.totalItems || 0,
              itemsPerPage: action.payload.pagination.itemsPerPage || 10,
            };
          }
        } else {
          state.products = action.payload;
        }
        state.error = null;
      })
      .addCase(searchProducts.rejected, (state, action) => {
        state.status = "failed";
        state.isSearchMode = true;
        state.isOffersMode = false;
        state.error =
          typeof action.payload === "string" ? action.payload : "Search failed";
      })
      .addCase(fetchOfferProducts.pending, (state) => {
        state.status = "loading";
        state.error = null;
        state.isOffersMode = true;
        state.isSearchMode = false;
      })
      .addCase(fetchOfferProducts.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.isOffersMode = true;
        state.isSearchMode = false;
        state.products = action.payload.data || action.payload;
        state.error = null;
      })
      .addCase(fetchOfferProducts.rejected, (state, action) => {
        state.status = "failed";
        state.isOffersMode = true;
        state.isSearchMode = false;
        state.error =
          typeof action.payload === "string"
            ? action.payload
            : "offer fetch get failed";
      });
  },
});

export const { clearProducts, setSearchMode, clearSearchMode, setOffersMode } =
  productSlice.actions;
export default productSlice.reducer;
